/**
 * THEME CUSTOM JAVASCRIPT
 * [@MOD-BC] Audio Control System (2025-10-30)
 *
 * Handles mute/unmute functionality for all video types:
 * - Native HTML5 videos
 * - YouTube embeds
 * - Vimeo embeds
 *
 * User Choice Preservation:
 * - Videos always start muted (privacy-first)
 * - If user manually unmutes, their choice persists through video loops
 * - This allows users to watch looping videos with sound without constant remuting
 *
 * Smart Tooltip System:
 * - Shows "Turn On Sound" tooltip on EVERY page load (max conversion)
 * - Displays after 1.5s (user is engaged)
 * - Shows for 8 seconds (on verge of pushy, optimized for conversion)
 * - Also shows on hover/focus (desktop only) for exploration
 * - Smart hide: If user hovers then leaves, hides immediately (respects user intent)
 * - Uses sessionStorage per-page to track timing
 * - 45-second cooldown before reshowing during same session
 * - Instantly dismissed when user unmutes
 * - Elegant frosted glass pill design with subtle effects
 */

(function() {
  'use strict';

  /**
   * Audio Control Manager
   * Manages mute/unmute state for video-media elements
   */
  class AudioControl {
    constructor(muteButton) {
      this.muteButton = muteButton;
      this.videoMedia = muteButton.closest('video-media');
      this.player = null;
      this.isMuted = true; // Always start muted (privacy-first)
      this.userUnmuted = false; // Track if user has manually unmuted
      this.tooltip = null; // Tooltip element
      this.tooltipTimer = null; // Timer for tooltip auto-hide
      this.isHovering = false; // Track hover state
      this.isAutoShown = false; // Track if tooltip was auto-shown (vs user-triggered)

      // Tooltip config
      this.TOOLTIP_DELAY = 1500; // Wait 1.5s before showing (user is watching)
      this.TOOLTIP_DURATION = 8000; // Show for 8 seconds (on the verge of pushy)
      this.TOOLTIP_COOLDOWN = 45000; // 45 seconds before showing again if still muted

      this.init();
    }

    init() {
      // Set initial muted state
      this.videoMedia.setAttribute('data-muted', 'true');

      // Create tooltip
      this.createTooltip();

      // Bind click handler
      this.muteButton.addEventListener('click', this.toggleMute.bind(this));

      // [@MOD-BC] Hover detection: hide tooltip immediately when hover ends (smart UX)
      this.muteButton.addEventListener('mouseenter', () => {
        this.isHovering = true;
      });

      this.muteButton.addEventListener('mouseleave', () => {
        this.isHovering = false;
        // Hide immediately if tooltip was auto-shown and user hovers off
        if (this.isAutoShown) {
          this.hideTooltip();
          this.clearTooltipTimers();
        }
      });

      // Listen for video play event to get player reference
      this.videoMedia.addEventListener('video:play', () => {
        this.getPlayer();
        this.scheduleTooltip();
      });

      // Hide tooltip when user unmutes
      this.videoMedia.addEventListener('video:unmuted', () => {
        this.hideTooltip();
        this.clearTooltipTimers();
      });
    }

    createTooltip() {
      this.tooltip = document.createElement('div');
      this.tooltip.className = 'audio-tooltip';
      this.tooltip.textContent = 'Turn On Sound';
      this.muteButton.appendChild(this.tooltip);
    }

    scheduleTooltip() {
      // Show on every page load, but respect cooldown during session
      const sessionKey = `audioTooltipShown_${window.location.pathname}`;
      const lastShown = sessionStorage.getItem(sessionKey);
      const now = Date.now();

      // Always show on fresh page load, OR if cooldown has passed during session
      const shouldShow = !lastShown || (now - parseInt(lastShown)) > this.TOOLTIP_COOLDOWN;

      if (shouldShow) {
        // Wait a bit to ensure user is watching
        setTimeout(() => {
          // Only show if still muted and video is playing
          if (this.isMuted && this.videoMedia.hasAttribute('playing')) {
            this.showTooltip();
            sessionStorage.setItem(sessionKey, now.toString());
          }
        }, this.TOOLTIP_DELAY);
      }
    }

    showTooltip() {
      if (!this.tooltip || this.userUnmuted) return;

      this.isAutoShown = true; // Mark as auto-shown for hover detection
      this.tooltip.classList.add('visible');

      // Auto-hide after duration (only if not hovering)
      this.tooltipTimer = setTimeout(() => {
        // Only auto-hide if user isn't currently hovering
        if (!this.isHovering) {
          this.hideTooltip();
        }
      }, this.TOOLTIP_DURATION);
    }

    hideTooltip() {
      if (this.tooltip) {
        this.tooltip.classList.remove('visible');
        this.isAutoShown = false; // Reset auto-shown flag
      }
    }

    clearTooltipTimers() {
      if (this.tooltipTimer) {
        clearTimeout(this.tooltipTimer);
        this.tooltipTimer = null;
      }
    }

    async getPlayer() {
      // Wait a moment for video to initialize
      await new Promise(resolve => setTimeout(resolve, 100));

      const hostType = this.videoMedia.getAttribute('host');

      if (hostType === 'youtube') {
        this.player = await this.getYouTubePlayer();
      } else if (hostType === 'vimeo') {
        this.player = await this.getVimeoPlayer();
      } else {
        this.player = this.videoMedia.querySelector('video');

        // Listen for native video loop/ended events to respect user's unmute choice
        if (this.player) {
          this.player.addEventListener('ended', () => {
            // Don't remute if user has manually unmuted
            if (!this.userUnmuted) {
              this.setMuted(true);
            }
          });
        }
      }

      // Ensure video starts muted (only on initial load)
      if (this.player && !this.userUnmuted) {
        this.setMuted(true);
      }
    }

    async getYouTubePlayer() {
      const iframe = this.videoMedia.querySelector('iframe');
      if (!iframe || !window.YT) return null;

      return new Promise((resolve) => {
        const player = new YT.Player(iframe, {
          events: {
            'onReady': () => resolve(player)
          }
        });
      });
    }

    async getVimeoPlayer() {
      const iframe = this.videoMedia.querySelector('iframe');
      if (!iframe || !window.Vimeo) return null;

      return new Vimeo.Player(iframe);
    }

    async toggleMute() {
      this.isMuted = !this.isMuted;

      // Track if user has manually unmuted
      if (!this.isMuted) {
        this.userUnmuted = true;
        this.hideTooltip();
        this.clearTooltipTimers();

        // Dispatch custom event
        this.videoMedia.dispatchEvent(new CustomEvent('video:unmuted', { bubbles: true }));
      }

      await this.setMuted(this.isMuted);
      this.updateUI();
    }

    async setMuted(muted) {
      if (!this.player) {
        await this.getPlayer();
      }

      if (!this.player) return;

      const hostType = this.videoMedia.getAttribute('host');

      try {
        if (hostType === 'youtube') {
          muted ? this.player.mute() : this.player.unMute();
        } else if (hostType === 'vimeo') {
          await this.player.setMuted(muted);
        } else {
          // Native HTML5 video
          this.player.muted = muted;
        }
      } catch (error) {
        console.warn('[@MOD-BC] Audio control error:', error);
      }
    }

    updateUI() {
      this.videoMedia.setAttribute('data-muted', String(this.isMuted));
      this.muteButton.setAttribute('aria-label', this.isMuted ? 'Unmute video' : 'Mute video');
    }
  }

  /**
   * Click-to-Pause Control
   * [@MOD-BC] Enables tap/click on video to pause/play
   */
  class ClickToPause {
    constructor(videoMedia) {
      this.videoMedia = videoMedia;
      this.init();
    }

    init() {
      // Only apply if enable-click-to-pause attribute is present
      if (!this.videoMedia.hasAttribute('enable-click-to-pause')) return;

      // Add click handler to the video itself
      const videoElement = this.videoMedia.querySelector('video, iframe');
      if (videoElement) {
        videoElement.addEventListener('click', this.handleClick.bind(this));
        // Ensure video element doesn't block pointer events on buttons
        videoElement.style.pointerEvents = 'auto';
        videoElement.style.cursor = 'pointer';
      }
    }

    handleClick(event) {
      // Don't trigger if clicking on buttons
      if (event.target.closest('.play-button, .mute-button, .restart-button')) {
        return;
      }

      // Toggle play/pause via the video-media element's native methods
      if (this.videoMedia.hasAttribute('playing')) {
        this.videoMedia.pause();
      } else {
        this.videoMedia.play();
      }
    }
  }

  /**
   * Restart Control
   * [@MOD-BC] Restarts video from beginning (minimal ghost button)
   */
  class RestartControl {
    constructor(restartButton) {
      this.restartButton = restartButton;
      this.videoMedia = restartButton.closest('video-media');
      this.player = null;

      this.init();
    }

    init() {
      this.restartButton.addEventListener('click', this.restart.bind(this));

      // Get player reference when video plays
      this.videoMedia.addEventListener('video:play', () => {
        this.getPlayer();
      });
    }

    async getPlayer() {
      if (!this.player) {
        try {
          this.player = await this.videoMedia.playerTarget();
        } catch (error) {
          console.warn('Failed to get video player:', error);
        }
      }
    }

    async restart() {
      await this.getPlayer();
      
      if (!this.player) return;

      const host = this.videoMedia.getAttribute('host');

      try {
        if (host === 'youtube') {
          // YouTube: seekTo(0) and play
          this.player.seekTo(0);
          this.player.playVideo();
        } else if (host === 'vimeo') {
          // Vimeo: setCurrentTime(0) and play
          await this.player.setCurrentTime(0);
          await this.player.play();
        } else {
          // Native HTML5 video
          this.player.currentTime = 0;
          await this.player.play();
        }
      } catch (error) {
        console.warn('Failed to restart video:', error);
      }
    }
  }

  /**
   * Initialize audio controls
   */
  function initAudioControls() {
    const muteButtons = document.querySelectorAll('.mute-button');

    muteButtons.forEach(button => {
      new AudioControl(button);
    });
  }

  /**
   * Initialize restart controls
   */
  function initRestartControls() {
    const restartButtons = document.querySelectorAll('.restart-button');

    restartButtons.forEach(button => {
      new RestartControl(button);
    });
  }

  /**
   * Initialize click-to-pause
   */
  function initClickToPause() {
    const videoMediaElements = document.querySelectorAll('video-media[enable-click-to-pause]');

    videoMediaElements.forEach(videoMedia => {
      new ClickToPause(videoMedia);
    });
  }

  /**
   * Initialize all controls
   */
  function initAllControls() {
    initAudioControls();
    initRestartControls();
    initClickToPause();
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllControls);
  } else {
    initAllControls();
  }

  // Re-initialize for dynamically added content (Shopify sections)
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', initAllControls);
  }

})();
